import { ReactNode, useEffect, useRef, useState } from 'react'

// Mounting a widget boots the federated remote behind it, so widgets below the
// fold are deferred until their slot is near the viewport. The observer's rect
// is clipped by the sidebar's own scroll container, which is what makes "near
// the viewport" mean near the visible part of the sidebar rather than the page.

type LazyWidgetMountProps = {
  /** rendered height of the slot, held by the placeholder so the sidebar's
   *  scroll height does not change when a widget mounts */
  height: number
  children: ReactNode
}

export const LazyWidgetMount = ({ height, children }: LazyWidgetMountProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const placeholderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = placeholderRef.current
    if (isVisible || !node) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      // start loading slightly before the slot scrolls into view
      { rootMargin: '200px' }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [isVisible])

  if (isVisible) return <>{children}</>

  return <div ref={placeholderRef} style={{ height }} />
}
